import { useState, useRef, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, X, Tag, Upload, Download, CheckCircle2, AlertCircle, Globe, Clock, ImageIcon, Loader2, Eye, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AdminLayout from "./AdminLayout";

interface GuideArticle {
  id: number;
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  scheduledAt: string | null;  // ISO datetime from DB
  isPublished: boolean;
  tags: string[];
  coverImage: string;
  coverImageAlt: string;
  readingMinutes: number;
  content: string;
  authorName?: string | null;
  authorType?: "Person" | "Organization";
  reviewedAt?: string | null;
  sourceUrls?: string[];
  viewCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

type FormData = {
  slug: string;
  title: string;
  description: string;
  scheduledAt: string;    // "YYYY-MM-DDTHH:mm" (datetime-local format, local/HKT)
  isPublished: boolean;
  tags: string[];
  coverImage: string;
  coverImageAlt: string;
  readingMinutes: number;
  content: string;
  authorName: string;
  authorType: "Person" | "Organization";
  reviewedAt: string | null;
  sourceUrls: string[];
};

/** Convert an ISO datetime string (UTC) to "YYYY-MM-DDTHH:mm" in the browser's local timezone */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format an ISO datetime string for display (local timezone) */
function formatScheduledDisplay(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}年${m}月${day}日 ${h}:${min}`;
}

function getStatus(a: GuideArticle): { label: string; color: string } {
  if (!a.isPublished) return { label: "草稿", color: "bg-gray-100 text-gray-600" };
  if (a.scheduledAt && new Date(a.scheduledAt) > new Date()) {
    return { label: "預排中", color: "bg-blue-50 text-blue-700" };
  }
  return { label: "已刊登", color: "bg-green-50 text-green-700" };
}

const emptyForm = (): FormData => ({
  slug: "",
  title: "",
  description: "",
  scheduledAt: toDatetimeLocal(new Date().toISOString()),
  isPublished: false,
  tags: [],
  coverImage: "",
  coverImageAlt: "",
  readingMinutes: 5,
  content: "",
  authorName: "",
  authorType: "Organization",
  reviewedAt: null,
  sourceUrls: [],
});

async function adminFetch(path: string, init?: RequestInit, token?: string | null) {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\u4e00-\u9fff-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

// ─── Cover Image Uploader ────────────────────────────────────────────────────
interface CoverImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  getToken: () => Promise<string | null>;
}

function CoverImageUploader({ value, onChange, getToken }: CoverImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("只接受圖片檔案（JPG、PNG、WebP 等）");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("圖片不可超過 10 MB");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/admin/guides/upload-image", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "上載失敗");
      }
      const { url } = await res.json() as { url: string };
      onChange(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }, [getToken, onChange]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  }, [uploadFile]);

  return (
    <div className="grid gap-2">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors cursor-pointer select-none
          ${uploading ? "pointer-events-none opacity-60" : ""}
          ${isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40"
          }
          ${value ? "py-3" : "py-8"}
        `}
      >
        {/* Preview thumbnail */}
        {value && (
          <div className="w-full px-3">
            <img
              src={value}
              alt="封面預覽"
              className="w-full max-h-40 object-cover rounded-md border"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          </div>
        )}

        {uploading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            上載中…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-center px-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ImageIcon className="w-4 h-4" />
              {value ? "重新上載圖片" : "拖拉或點擊上載封面圖片"}
            </div>
            {!value && (
              <p className="text-xs text-muted-foreground/70">支援 JPG、PNG、WebP，最大 10 MB</p>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}

      {/* Manual URL fallback */}
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">或輸入網址</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/uploads/guides/my-cover.jpg 或完整 URL"
        className="text-xs font-mono"
      />
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminGuidesPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const af = async (path: string, init?: RequestInit) => adminFetch(path, init, await getToken());

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<GuideArticle | null>(null);
  const [previewArticle, setPreviewArticle] = useState<GuideArticle | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [tagInput, setTagInput] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // JSON import state
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);

  const { data: articles = [], isLoading, isError, error } = useQuery<GuideArticle[]>({
    queryKey: ["admin-guides"],
    queryFn: () => af("/api/admin/guides"),
  });
  const { data: viewTrackingIp, refetch: refreshViewTrackingIp } = useQuery<{
    ip: string | null;
    ipExcluded: boolean;
    adminExcluded: boolean;
  }>({
    queryKey: ["admin-guides-view-tracking-ip"],
    queryFn: () => af("/api/admin/guides/view-tracking-ip"),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const save = useMutation({
    mutationFn: (data: FormData & { id?: number }) => {
      const { id, scheduledAt, ...rest } = data;
      // Convert datetime-local (local/HKT) to UTC ISO string for the server
      const scheduledAtISO = scheduledAt ? new Date(scheduledAt).toISOString() : new Date().toISOString();
      return af(id ? `/api/admin/guides/${id}` : "/api/admin/guides", {
        method: id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...rest, sourceUrls: rest.sourceUrls.map(url => url.trim()).filter(Boolean), scheduledAt: scheduledAtISO }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-guides"] });
      qc.invalidateQueries({ queryKey: ["guides"] });
      setOpen(false);
    },
  });

  const togglePublish = useMutation({
    mutationFn: (a: GuideArticle) =>
      af(`/api/admin/guides/${a.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: a.slug,
          title: a.title,
          description: a.description,
          scheduledAt: a.scheduledAt ?? new Date().toISOString(),
          isPublished: !a.isPublished,
          tags: a.tags,
          coverImage: a.coverImage,
          coverImageAlt: a.coverImageAlt,
          readingMinutes: a.readingMinutes,
          content: a.content,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-guides"] });
      qc.invalidateQueries({ queryKey: ["guides"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => af(`/api/admin/guides/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-guides"] });
      qc.invalidateQueries({ queryKey: ["guides"] });
      setDeleteId(null);
    },
  });

  const batchImport = useMutation({
    mutationFn: (data: { articles: unknown[] }) =>
      af("/api/admin/guides/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: (result: { imported: number; skipped: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-guides"] });
      qc.invalidateQueries({ queryKey: ["guides"] });
      setImportResult(result);
    },
  });

  function openAdd() {
    setForm(emptyForm());
    setEditing(null);
    setTagInput("");
    setOpen(true);
  }

  function openEdit(a: GuideArticle) {
    setEditing(a);
    setForm({
      slug: a.slug,
      title: a.title,
      description: a.description,
      scheduledAt: a.scheduledAt
        ? toDatetimeLocal(a.scheduledAt)
        : toDatetimeLocal(new Date().toISOString()),
      isPublished: a.isPublished ?? false,
      tags: a.tags ?? [],
      coverImage: a.coverImage,
      coverImageAlt: a.coverImageAlt,
      readingMinutes: a.readingMinutes,
      content: a.content,
      authorName: a.authorName ?? "",
      authorType: a.authorType ?? "Organization",
      reviewedAt: a.reviewedAt ?? null,
      sourceUrls: a.sourceUrls ?? [],
    });
    setTagInput("");
    setOpen(true);
  }

  function handleTitleChange(title: string) {
    setForm((f) => ({
      ...f,
      title,
      ...(editing == null && !f.slug ? { slug: slugify(title) } : {}),
    }));
  }

  function addTag() {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
  }

  function removeTag(tag: string) {
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    save.mutate({ ...form, ...(editing ? { id: editing.id } : {}) });
  }

  function handleExport() {
    const exportData = articles.map(({ id: _id, createdAt: _c, updatedAt: _u, ...rest }) => rest);
    const json = JSON.stringify(exportData, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      alert(`已複製 ${exportData.length} 篇文章的 JSON 到剪貼簿`);
    }).catch(() => {
      setImportJson(json);
      setImportOpen(true);
    });
  }

  function openImport() {
    setImportJson("");
    setImportError("");
    setImportResult(null);
    setImportOpen(true);
  }

  function handleImportSubmit() {
    setImportError("");
    setImportResult(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(importJson.trim());
    } catch {
      setImportError("JSON 格式錯誤，請確認是合法的 JSON");
      return;
    }
    const arr = Array.isArray(parsed) ? parsed : (parsed as Record<string, unknown>)["articles"];
    if (!Array.isArray(arr) || arr.length === 0) {
      setImportError("請提供文章陣列（[ {...}, {...} ]）或 { articles: [...] } 格式");
      return;
    }
    batchImport.mutate({ articles: arr });
  }

  const deleteTarget = articles.find((a) => a.id === deleteId);

  return (
    <AdminLayout tab="guides">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">採購知識中心文章</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleExport} disabled={articles.length === 0}>
            <Download className="h-4 w-4 mr-1.5" /> 匯出 JSON
          </Button>
          <Button size="sm" variant="outline" onClick={openImport}>
            <Upload className="h-4 w-4 mr-1.5" /> 匯入 JSON
          </Button>
          <Button size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1.5" /> 新增文章
          </Button>
        </div>
      </div>

      {viewTrackingIp && (
        <p className="mb-4 text-sm text-muted-foreground">
          目前伺服器識別 IP：{viewTrackingIp.ip ?? "無法辨認"}；
          {viewTrackingIp.ipExcluded ? "此 IP 已排除閱讀次數" : "此 IP 未列入排除名單"}。
          {viewTrackingIp.adminExcluded && " 已登入管理員閱讀亦不計數。"}
          <button type="button" className="ml-2 text-primary underline" onClick={() => void refreshViewTrackingIp()}>
            重新檢查 IP
          </button>
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">ID</TableHead>
                <TableHead>標題</TableHead>
                <TableHead className="w-[70px] max-w-[70px]">Slug</TableHead>
                <TableHead className="whitespace-nowrap">狀態</TableHead>
                <TableHead>刊登時間</TableHead>
                <TableHead>標籤</TableHead>
                <TableHead className="w-20 text-right">閱讀次數</TableHead>
                <TableHead className="w-40">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    載入中...
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-red-500">
                    載入失敗：{(error as Error)?.message ?? "伺服器錯誤"}
                  </TableCell>
                </TableRow>
              ) : articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    暫無文章，請點擊「新增文章」或「匯入 JSON」
                  </TableCell>
                </TableRow>
              ) : (
                articles.map((a) => {
                  const status = getStatus(a);
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="text-muted-foreground text-xs">{a.id}</TableCell>
                      <TableCell className="max-w-[180px]">
                        <button
                          type="button"
                          className="block max-w-full truncate text-left font-medium hover:text-primary hover:underline"
                          onClick={() => setPreviewArticle(a)}
                          title="預覽文章（不計入閱讀次數）"
                        >
                          {a.title}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground w-[70px] max-w-[70px] truncate font-mono">
                        {a.slug}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className={`inline-flex whitespace-nowrap items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${status.color}`}>
                          {a.isPublished
                            ? (a.scheduledAt && new Date(a.scheduledAt) > new Date()
                              ? <Clock className="w-3 h-3" />
                              : <Globe className="w-3 h-3" />)
                            : null}
                          {status.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                        {formatScheduledDisplay(a.scheduledAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(a.tags ?? []).slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">
                              {tag}
                            </Badge>
                          ))}
                          {(a.tags ?? []).length > 2 && (
                            <span className="text-xs text-muted-foreground">+{(a.tags ?? []).length - 2}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium">
                        {a.viewCount ?? 0}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {/* Quick publish toggle */}
                          <button
                            title={a.isPublished ? "點擊取消刊登" : "點擊刊登"}
                            onClick={() => togglePublish.mutate(a)}
                            disabled={togglePublish.isPending}
                            className={`h-7 px-2 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                              a.isPublished
                                ? "bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-600"
                                : "bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700"
                            }`}
                          >
                            {a.isPublished ? "已刊登" : "草稿"}
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                            title="預覽文章（不計入閱讀次數）"
                            aria-label={`預覽文章：${a.title}`}
                            onClick={() => setPreviewArticle(a)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEdit(a)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteId(a.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Uses article data already fetched from the admin API; it never calls the public view-tracking endpoint. */}
      <Dialog open={previewArticle !== null} onOpenChange={(isOpen) => { if (!isOpen) setPreviewArticle(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          {previewArticle && (
            <>
              <DialogHeader className="px-6 pt-6">
                <div className="flex items-center gap-2">
                  <DialogTitle>文章預覽</DialogTitle>
                  <Badge variant="secondary" className="text-xs font-normal">不計入閱讀次數</Badge>
                </div>
              </DialogHeader>

              {previewArticle.coverImage && (
                <img
                  src={previewArticle.coverImage}
                  alt={previewArticle.coverImageAlt}
                  className="w-full max-h-72 object-cover"
                />
              )}

              <div className="px-6 pb-6">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(previewArticle.tags ?? []).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                    >
                      <Tag className="w-2.5 h-2.5" />
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl font-bold leading-snug mb-3">{previewArticle.title}</h1>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {previewArticle.description}
                </p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground border-b pb-4 mb-6">
                  <Calendar className="w-3 h-3" />
                  {formatScheduledDisplay(previewArticle.scheduledAt ?? previewArticle.publishedAt)}
                </div>

                <div className="prose prose-sm max-w-none
                  prose-headings:font-bold prose-headings:text-foreground
                  prose-h2:text-lg prose-h2:border-b prose-h2:pb-2 prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground prose-p:leading-relaxed
                  prose-strong:text-foreground
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-ul:text-foreground prose-ol:text-foreground
                  prose-li:text-foreground prose-li:leading-relaxed
                  prose-table:text-sm prose-table:border prose-table:rounded-lg
                  prose-thead:bg-muted/60 prose-th:px-4 prose-th:py-3
                  prose-th:font-semibold prose-th:text-left prose-td:px-4 prose-td:py-3
                  prose-tr:border-b prose-hr:border-border
                ">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ children }) => (
                        <div className="overflow-x-auto rounded-lg border my-4">
                          <table className="w-full text-sm">{children}</table>
                        </div>
                      ),
                    }}
                  >
                    {previewArticle.content || "（文章尚未有內容）"}
                  </ReactMarkdown>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "編輯文章" : "新增文章"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Title */}
            <div className="grid gap-1.5">
              <Label htmlFor="ga-title">標題 *</Label>
              <Input
                id="ga-title"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="文章標題"
                required
              />
            </div>

            {/* Slug */}
            <div className="grid gap-1.5">
              <Label htmlFor="ga-slug">
                Slug *{" "}
                <span className="text-xs text-muted-foreground font-normal">（URL 路徑，英文/數字/連字號）</span>
              </Label>
              <Input
                id="ga-slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="hk-school-procurement-guide"
                required
                className="font-mono text-sm"
              />
            </div>

            {/* Description */}
            <div className="grid gap-1.5">
              <Label htmlFor="ga-desc">摘要 *</Label>
              <Textarea
                id="ga-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="文章摘要（在列表頁及 SEO 顯示）"
                rows={3}
                required
              />
            </div>

            {/* Scheduled publish datetime + reading minutes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="ga-scheduled">
                  刊登日期時間 *{" "}
                  <span className="text-xs text-muted-foreground font-normal">（香港時間）</span>
                </Label>
                <Input
                  id="ga-scheduled"
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  到達此時間後、且已開啟刊登，文章才會出現在網站上。
                </p>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ga-mins">閱讀時間（分鐘）</Label>
                <Input
                  id="ga-mins"
                  type="number"
                  min={1}
                  max={60}
                  value={form.readingMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, readingMinutes: parseInt(e.target.value) || 5 }))}
                />
              </div>
            </div>

            {/* Publish toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <Switch
                id="ga-published"
                checked={form.isPublished}
                onCheckedChange={(v) => setForm((f) => ({ ...f, isPublished: v }))}
              />
              <div>
                <Label htmlFor="ga-published" className="cursor-pointer font-medium">
                  {form.isPublished ? "已開啟刊登" : "草稿（未刊登）"}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {form.isPublished
                    ? "到達刊登時間後，文章將自動在網站顯示。"
                    : "開啟後，到達刊登時間，文章才會出現在網站。"}
                </p>
              </div>
            </div>

            <fieldset className="grid gap-3 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">作者、審閱及參考來源（公開顯示）</legend>
              <p className="text-xs text-muted-foreground">只填寫真實資料；留空不會虛構作者或將修改日期當作已審閱。請勿在來源中填寫付費招標文件、私人或含登入資料的連結。</p>
              <Label htmlFor="ga-author">作者／編輯署名</Label>
              <Input id="ga-author" maxLength={150} value={form.authorName} onChange={e => setForm(f => ({ ...f, authorName: e.target.value }))} />
              <Label htmlFor="ga-author-type">署名類型</Label>
              <select id="ga-author-type" className="h-10 rounded-md border bg-background px-3 text-sm" value={form.authorType}
                onChange={e => setForm(f => ({ ...f, authorType: e.target.value as "Person" | "Organization" }))}>
                <option value="Organization">編輯團隊／機構</option><option value="Person">個人作者</option>
              </select>
              <Label htmlFor="ga-reviewed">最後實際審閱日期</Label>
              <Input id="ga-reviewed" type="date" value={form.reviewedAt ?? ""} onChange={e => setForm(f => ({ ...f, reviewedAt: e.target.value || null }))} />
              <Label htmlFor="ga-sources">參考來源網址（每行一個）</Label>
              <Textarea id="ga-sources" value={form.sourceUrls.join("\n")} onChange={e => setForm(f => ({ ...f, sourceUrls: e.target.value.split("\n") }))} />
            </fieldset>

            {/* Cover image */}
            <div className="grid gap-1.5">
              <Label>封面圖片</Label>
              <CoverImageUploader
                value={form.coverImage}
                onChange={(url) => setForm((f) => ({ ...f, coverImage: url }))}
                getToken={getToken}
              />
            </div>

            {/* Cover image alt */}
            <div className="grid gap-1.5">
              <Label htmlFor="ga-alt">封面圖片說明文字（Alt）</Label>
              <Input
                id="ga-alt"
                value={form.coverImageAlt}
                onChange={(e) => setForm((f) => ({ ...f, coverImageAlt: e.target.value }))}
                placeholder="圖片的描述性文字（有助 SEO 與無障礙）"
              />
            </div>

            {/* Tags */}
            <div className="grid gap-1.5">
              <Label>標籤</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="輸入標籤後按 Enter"
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>
                  <Tag className="h-4 w-4 mr-1" /> 加入
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {form.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Content (Markdown) */}
            <div className="grid gap-1.5">
              <Label htmlFor="ga-content">內文（Markdown 格式）*</Label>
              <Textarea
                id="ga-content"
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder={`## 標題\n\n段落文字…\n\n| 欄位 | 說明 |\n|---|---|\n| 值 | 描述 |`}
                rows={16}
                required
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                支援 Markdown 語法：**粗體**、*斜體*、# 標題、- 列表、| 表格 |、[連結](url)
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                取消
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "儲存中…" : editing ? "更新文章" : "新增文章"}
              </Button>
            </DialogFooter>

            {save.isError && (
              <p className="text-red-500 text-sm text-center">
                儲存失敗：{(save.error as Error)?.message}
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* JSON Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!v) { setImportOpen(false); setImportResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>匯入文章 JSON</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              貼上文章 JSON 陣列（格式：<code className="bg-muted px-1 rounded text-xs">[{"{...}"}, {"{...}"}]</code>）。
              已存在相同 slug 的文章會自動略過，不會覆蓋。
            </p>

            {importResult ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-semibold">匯入完成</p>
                <p className="text-sm text-muted-foreground text-center">
                  成功匯入 <span className="font-bold text-foreground">{importResult.imported}</span> 篇文章
                  {importResult.skipped > 0 && <>，略過 <span className="font-bold text-foreground">{importResult.skipped}</span> 篇（slug 重複）</>}
                </p>
                <Button onClick={() => { setImportOpen(false); setImportResult(null); }}>完成</Button>
              </div>
            ) : (
              <>
                <Textarea
                  value={importJson}
                  onChange={(e) => { setImportJson(e.target.value); setImportError(""); }}
                  placeholder={'[\n  {\n    "slug": "my-article",\n    "title": "文章標題",\n    "description": "摘要",\n    "scheduledAt": "2026-09-01T09:00:00+08:00",\n    "isPublished": true,\n    "tags": ["標籤"],\n    "coverImage": "/image.jpg",\n    "coverImageAlt": "圖片說明",\n    "readingMinutes": 5,\n    "content": "## 內文\\n\\n..."\n  }\n]'}
                  rows={16}
                  className="font-mono text-xs"
                />

                {importError && (
                  <div className="flex items-center gap-2 text-red-500 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {importError}
                  </div>
                )}

                {batchImport.isError && (
                  <div className="flex items-center gap-2 text-red-500 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    匯入失敗：{(batchImport.error as Error)?.message}
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>
                    取消
                  </Button>
                  <Button
                    onClick={handleImportSubmit}
                    disabled={!importJson.trim() || batchImport.isPending}
                  >
                    <Upload className="h-4 w-4 mr-1.5" />
                    {batchImport.isPending ? "匯入中…" : "確認匯入"}
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteId !== null} onOpenChange={(v) => { if (!v) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定刪除文章？</AlertDialogTitle>
            <AlertDialogDescription>
              將永久刪除「{deleteTarget?.title}」，此動作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deleteId !== null && remove.mutate(deleteId)}
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
